import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description"; // generic doc
import TableChartIcon from "@mui/icons-material/TableChart"; // spreadsheet
import ImageIcon from "@mui/icons-material/Image"; // images
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"; // fallback
import DeleteIcon from "@mui/icons-material/Delete";
import { useError } from "../../Context/errorContext";
import config from "../../Config/config";
import { options } from "../helpers";

const Attachments = ({
  allowedTypes,
  allowedExtensions,
  uploads,
  setUploads,
  uid,
}) => {
  const [category, setCategory] = useState(null);
  const { setAlert } = useError();

  function getFileIcon(type) {
    if (!type) return <InsertDriveFileIcon color="action" />;

    if (type === "application/pdf") return <PictureAsPdfIcon color="error" />;

    if (
      type === "application/msword" ||
      type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return <DescriptionIcon color="primary" />;
    }

    if (
      type === "application/vnd.ms-excel" ||
      type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      type === "text/csv"
    ) {
      return <TableChartIcon color="success" />;
    }

    if (type.startsWith("image/")) {
      return <ImageIcon color="secondary" />;
    }

    return <InsertDriveFileIcon color="action" />;
  }

  function handleFiles(fileList, cat) {
    const files = Array.from(fileList);
    const MAX_MB = 10;

    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      const typeOk = allowedTypes?.has ? allowedTypes.has(file.type) : true; // if no validator passed, allow all
      const sizeOk = file.size <= MAX_MB * 1024 * 1024;

      if (!typeOk)
        errors.push(
          `${file.name}: unsupported file type (${file.type || "unknown"})`,
        );
      if (!sizeOk) errors.push(`${file.name}: exceeds ${MAX_MB} MB`);

      if (typeOk && sizeOk) {
        validFiles.push({
          id: `${Date.now()}`,
          file,
          category: cat ?? null,
          previewUrl: URL.createObjectURL(file),
        });
      }
    });

    if (errors.length) {
      console.error("File errors:", errors);
      alert(errors.join("\n"));
    }

    setUploads((prev) => [...prev, ...validFiles]);
    setCategory(null);
  }

  const removeById = (id) => {
    setUploads((prev) => {
      const next = [...prev];
      const item = next.find((u) => u.id === id);
      if (item.uid) {
        item.deleted = true;
        return next;
      }
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return next.filter((u) => u.id !== id);
    });
  };

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      uploads.forEach((p) => {
        if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl);
      });
    };
  }, [uploads]);

  return (
    <Box
      sx={{
        position: "relative",
        border: "1px solid rgba(0, 0, 0, 0.23)", // matches TextField default
        borderRadius: 1,
        padding: 2,
        marginTop: 2,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          top: 0,
          left: 8,
          transform: "translateY(-50%)",
          backgroundColor: "white",
          paddingX: 0.5,
          color: "rgba(0, 0, 0, 0.6)", // MUI label default
          fontSize: "0.75rem",
          lineHeight: 1,
        }}
      >
        Attachments
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ pb: 3 }}>
        <Autocomplete
          size="small"
          options={options}
          value={category}
          onChange={(_, val) => setCategory(val)}
          sx={{ minWidth: 180 }}
          renderInput={(params) => (
            <TextField {...params} label="Select Type" />
          )}
        />

        <Button component="label" variant="contained" disabled={!category}>
          Add Attachment
          <input
            hidden
            type="file"
            accept={Array.from([...allowedTypes, ...allowedExtensions]).join(
              ",",
            )}
            onChange={(e) => {
              if (e.target.files?.length) {
                handleFiles(e.target.files, category);
                e.target.value = ""; // allow picking the same file again
              }
            }}
          />
        </Button>
      </Stack>

      <Box sx={{ minWidth: 0 }}>
        {uploads.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No attachments yet
          </Typography>
        ) : (
          <Stack spacing={1}>
            {uploads
              .filter((u) => u.deleted !== true)
              .map((u) => {
                const url = `${config.serverBaseUrl}/projects/attachments/${uid}/${u.uid}/download/${u.file.name}`;
                return (
                  <Stack
                    key={u.id}
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    justifyItems="start"
                    sx={{ minWidth: 0 }}
                  >
                    {getFileIcon(u.file.type)}
                    <Link
                      href={u.previewUrl ?? url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        if (u.uid) {
                          e.preventDefault();
                          window.open(url, "_blank", "noopener,noreferrer");
                        }
                      }}
                      underline="hover"
                      sx={{
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {u.file.name}
                    </Link>
                    {u.category && <Chip size="small" label={u.category} />}
                    <Tooltip title="Remove attachment">
                      <IconButton
                        aria-label={`Remove ${u.file.name}`}
                        size="small"
                        onClick={() => removeById(u.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                );
              })}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default Attachments;
